// A question the agent asked, kept in the conversation. A question used to
// live only in the card under the chat, so once it was answered, withdrawn or
// the page reloaded, there was no trace of what had been asked.
export interface AutoAgentAskRecord {
  question_id: string
  state: 'open' | 'answered' | 'withdrawn'
}

/** Record a question in the conversation once (a reconnect re-sends it), or
 *  move an existing record on from 'open'. A record that is already answered
 *  or withdrawn never changes again, and only an 'open' event adds one. */
export function upsertAskReply<R extends { ask?: AutoAgentAskRecord }>(
  replies: R[],
  ask: AutoAgentAskRecord,
  make: () => R,
): R[] {
  const i = replies.findIndex((r) => r.ask?.question_id === ask.question_id)
  if (i < 0) return ask.state === 'open' ? [...replies, make()] : replies
  const cur = replies[i].ask!
  if (cur.state === ask.state || cur.state !== 'open') return replies
  const next = replies.slice()
  next[i] = { ...replies[i], ask: { ...cur, state: ask.state } }
  return next
}

/** Claude Code / Codex docks: a message typed into the main box while a
 *  question card is open is the reply to it — the agent reads it as the next
 *  turn either way. Retire every open card with that text, the same as a click
 *  on the card does, so its options stop offering an answer already given (and
 *  an open card no longer keeps forcing the dock panel open). Returns the same
 *  array when nothing is open. */
export function answerOpenAsks<
  M extends { role: string; askAnswered?: boolean; askAnsweredWith?: string },
>(messages: M[], text: string): M[] {
  if (!text || !messages.some((m) => m.role === 'ask' && !m.askAnswered)) return messages
  return messages.map((m) =>
    m.role === 'ask' && !m.askAnswered ? { ...m, askAnswered: true, askAnsweredWith: text } : m,
  )
}
